/**
 * Error Tracking Utility
 * 
 * This module provides centralized error tracking functionality.
 * Currently uses console logging, but can be easily extended to integrate
 * with services like Sentry, LogRocket, or similar.
 */

interface ErrorContext {
  userId?: string;
  userEmail?: string;
  subscriptionStatus?: string;
  path?: string;
  referrer?: string;
  userAgent?: string;
  timestamp?: string;
  [key: string]: unknown;
}

class ErrorTracker {
  private initialized = false;
  private userContext: ErrorContext = {};

  /**
   * Initialize error tracking
   * This will be called when error tracking service is integrated
   */
  init(config?: { dsn?: string; environment?: string }) {
    if (this.initialized) return;

    // Set up global error handlers
    if (typeof window !== 'undefined') {
      // Capture unhandled errors
      window.addEventListener('error', (event) => {
        this.captureException(event.error || new Error(event.message), {
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
          type: 'unhandled_error',
        });
      });

      // Capture unhandled promise rejections
      window.addEventListener('unhandledrejection', (event) => {
        this.captureException(
          event.reason instanceof Error 
            ? event.reason 
            : new Error(String(event.reason)),
          {
            type: 'unhandled_promise_rejection',
          }
        );
      });
    }

    this.initialized = true;
    console.log('Error tracking initialized');
  }

  /**
   * Set user context for error tracking
   */
  setUser(userId?: string, email?: string, subscriptionStatus?: string) {
    this.userContext = {
      userId,
      userEmail: email,
      subscriptionStatus,
    };
  }

  /**
   * Clear user context
   */
  clearUser() {
    this.userContext = {};
  }

  /**
   * Capture an exception/error
   */
  captureException(error: Error, context?: ErrorContext) {
    const errorContext: ErrorContext = {
      ...this.userContext,
      ...context,
      path: typeof window !== 'undefined' ? window.location.pathname : undefined,
      referrer: typeof document !== 'undefined' ? document.referrer : undefined,
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
      timestamp: new Date().toISOString(),
    };

    // Log to console (always)
    console.error('Error captured:', {
      message: error.message,
      stack: error.stack,
      context: errorContext,
    });

    // TODO: Send to error tracking service
    // Example for Sentry:
    // if (window.Sentry) {
    //   window.Sentry.captureException(error, {
    //     contexts: { custom: errorContext },
    //     user: {
    //       id: errorContext.userId,
    //       email: errorContext.userEmail,
    //     },
    //     tags: {
    //       subscription_status: errorContext.subscriptionStatus,
    //     },
    //   });
    // }

    // Example for LogRocket:
    // if (window.LogRocket) {
    //   window.LogRocket.captureException(error, {
    //     tags: errorContext,
    //   });
    // }
  }

  /**
   * Capture a message (non-error)
   */
  captureMessage(message: string, level: 'info' | 'warning' | 'error' = 'info', context?: ErrorContext) {
    const messageContext: ErrorContext = {
      ...this.userContext,
      ...context,
      path: typeof window !== 'undefined' ? window.location.pathname : undefined,
      timestamp: new Date().toISOString(),
    };

    // Log to console
    const logMethod = level === 'error' ? console.error : level === 'warning' ? console.warn : console.info;
    logMethod('Message captured:', {
      message,
      level,
      context: messageContext,
    });

    // TODO: Send to error tracking service
  }

  /**
   * Add breadcrumb for debugging
   */
  addBreadcrumb(message: string, category?: string, level: 'info' | 'warning' | 'error' = 'info', data?: unknown) {
    // TODO: Send breadcrumb to error tracking service
    // For now, just log in development
    if (process.env.NODE_ENV === 'development') {
      console.log('Breadcrumb:', { message, category, level, data });
    }
  }
}

// Export singleton instance
export const errorTracking = new ErrorTracker();

// Auto-initialize in production
if (typeof window !== 'undefined' && process.env.NODE_ENV === 'production') {
  errorTracking.init();
}

